import React from "react";
import dayjs from "dayjs";
import { Box, Flex, Spinner, Stack, Text, Tooltip, useColorModeValue } from "@chakra-ui/react";
import {
    REFUND_OUTCOME_LABELS, REFUND_OUTCOME_COLORS, REFUND_SOURCE_LABELS, DEFAULT_CHIP_COLOR,
} from "./paymentsConstants";
import useRefundsHistory from "./useRefundsHistory";
import SchemeBadge from "./SchemeBadge";

/**
 * Formats an ISO timestamp as DD/MM/YYYY HH:mm or "—".
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatTimestamp(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Formats an amount as fixed-decimal currency.
 * @param {number|null|undefined} amount - Amount value
 * @returns {string}
 */
function formatAttemptAmount(amount) {
    if (amount === null || amount === undefined) return "—";
    return Number(amount).toFixed(2);
}

/**
 * Renders the outcome badge for one attempt.
 * @param {Object} props - { outcome }
 * @returns {React.ReactElement}
 */
function OutcomeChip({ outcome }) {
    const scheme = REFUND_OUTCOME_COLORS[outcome] || DEFAULT_CHIP_COLOR;
    const label = REFUND_OUTCOME_LABELS[outcome] || outcome || "—";
    return <SchemeBadge scheme={scheme} label={label} />;
}

/**
 * Renders the error code with full message tooltip.
 * @param {Object} props - { attempt }
 * @returns {React.ReactElement|null}
 */
function ErrorLine({ attempt }) {
    if (!attempt.error_code && !attempt.error_message) return null;
    const message = attempt.error_message || "";
    return (
        <Tooltip label={message} placement="top" hasArrow>
            <Text color="red.400" fontFamily="mono" fontSize="0.72rem" cursor="help">
                {attempt.error_code || "—"} — {message.slice(0, 80)}{message.length > 80 ? "…" : ""}
            </Text>
        </Tooltip>
    );
}

/**
 * Renders the top line of an attempt: outcome, source, amount and timestamp.
 * @param {Object} props - { attempt }
 * @returns {React.ReactElement}
 */
function AttemptTopLine({ attempt }) {
    const sourceColor = useColorModeValue("secondaryGray.900", "white");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const hasAmount = attempt.amount !== null && attempt.amount !== undefined;
    return (
        <Flex justify="space-between" align="center" gap="8px" mb="4px">
            <Flex align="center" gap="8px" flexWrap="wrap">
                <OutcomeChip outcome={attempt.outcome} />
                <Text fontSize="0.75rem" fontWeight="600" color={sourceColor}>
                    {REFUND_SOURCE_LABELS[attempt.source] || attempt.source || "—"}
                </Text>
                {hasAmount && <Text fontSize="0.72rem" color={subColor}>• {formatAttemptAmount(attempt.amount)}</Text>}
            </Flex>
            <Text fontSize="0.7rem" color={subColor}>{formatTimestamp(attempt.created_at)}</Text>
        </Flex>
    );
}

/**
 * Renders a single attempt row.
 * @param {Object} props - { attempt }
 * @returns {React.ReactElement}
 */
function AttemptRow({ attempt }) {
    const rowBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const subColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="12px" borderRadius="12px" bg={rowBg}>
            <AttemptTopLine attempt={attempt} />
            <ErrorLine attempt={attempt} />
            {attempt.triggered_by_admin_id && (
                <Text fontSize="0.7rem" color={subColor} mt="2px">Déclenché par admin #{attempt.triggered_by_admin_id}</Text>
            )}
        </Box>
    );
}

/**
 * Refund attempts history block (used inside the detail drawer).
 * Lazily fetches the history when paymentId becomes truthy.
 * @param {Object} props - { paymentId }
 * @returns {React.ReactElement}
 */
function RefundsHistoryList({ paymentId }) {
    const { attempts, loading, error } = useRefundsHistory(paymentId);
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box>
            <Text fontSize="sm" fontWeight="700" color={titleColor} mb="8px">Historique des tentatives</Text>
            {loading && <Flex justify="center" py="16px"><Spinner size="sm" color="brand.500" /></Flex>}
            {error && !loading && <Text color="red.500" fontSize="sm">{typeof error === "string" ? error : "Erreur de chargement"}</Text>}
            {!loading && !error && attempts.length === 0 && <Text color={emptyColor} fontSize="sm">Aucune tentative enregistrée</Text>}
            {!loading && attempts.length > 0 && (
                <Stack spacing="8px">{attempts.map((a) => <AttemptRow key={a.id} attempt={a} />)}</Stack>
            )}
        </Box>
    );
}

export default RefundsHistoryList;
